<template>
    <div class="bg-darkpurple h-full">
        <div v-if="!passwordVisible">
            <div class="bg-verydarkpurple text-primarytext text-center py-4 border-b-2 border-t-2 border-primarytext">
                <p>If you click on an image, a button will appear that lets you download the full resolution version of that image.</p>
            </div>

            <div class="px-8 pt-8">
                <div class="flex flex-wrap justify-center gap-2">
                    <button
                        v-for="year in years"
                        :key="year"
                        @click="selectYear(year)"
                        class="px-4 py-2 rounded text-white font-extrabold transition duration-150 ease-in-out"
                        :class="year === selectedYear ? 'bg-purple-800 ring-2 ring-primarytext' : 'bg-purple-500 hover:bg-purple-600'"
                    >
                        {{ year }}
                    </button>
                </div>

                <div class="flex flex-wrap justify-center gap-2 mt-3">
                    <button
                        v-for="month in monthsInSelectedYear"
                        :key="month.index"
                        @click="selectMonth(month.index)"
                        class="px-3 py-1 rounded text-white font-bold text-sm transition duration-150 ease-in-out"
                        :class="month.index === selectedGroupIndex ? 'bg-purple-800 ring-2 ring-primarytext' : 'bg-purple-500 hover:bg-purple-600'"
                    >
                        {{ month.label }}
                    </button>
                </div>
            </div>

            <div v-if="isLoadingGroup" class="text-primarytext text-center mt-8">
                Loading pictures... {{ loadedCount }} / {{ totalCount }}
            </div>

            <div v-if="selectedGroupIndex !== null" class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 m-8">
                <div
                    v-for="(img, index) in pictures[selectedGroupIndex]"
                    :key="index"
                    v-show="img"
                    class="flex flex-col items-center justify-center">
                    <img
                        :src="img"
                        :alt="imageName(selectedGroupIndex, index)"
                        class="object-cover rounded-3xl w-full h-auto cursor-pointer border-4 border-transparent hover:border-purple-500 transition duration-150 ease-in-out"
                        @click="selectImage(selectedGroupIndex, index)"
                    >
                    <button
                        v-if="selectedImage && selectedImage.groupIndex === selectedGroupIndex && selectedImage.imageIndex === index"
                        @click="downloadHighRes(selectedGroupIndex, index)"
                        class="mt-2 px-2 py-0 bg-purple-700 text-white text-lg rounded shadow hover:bg-purple-800 active:bg-purple-900 focus:outline-none transition duration-150 ease-in-out"
                    >
                        Download in Full Resolution
                    </button>
                </div>
            </div>
        </div>

        <div class="mx-auto p-4 h-screen flex flex-col items-center justify-center" v-if="passwordVisible">
            <input
                v-model="password"
                type="password"
                placeholder="Enter the super secret password"
                class="border p-2 rounded w-full mb-4 max-w-xs text-center"
                :class="['text-center p-2 border rounded', shake ? 'animate-shake' : '', loading ? 'bg-gray-300 cursor-not-allowed' : '']"
                @keydown.enter="handleFetchCredentials"
                :disabled="loading"
            >

            <button
                class="bottom-2 right-2 px-3 py-1 bg-purple-600 text-white text-sm rounded shadow hover:bg-purple-700 active:bg-purple-800 focus:outline-none transition duration-150 ease-in-out"
                @click="handleFetchCredentials"
                :disabled="loading"
                :class="{ 'bg-gray-300 hover:bg-gray-700 cursor-not-allowed': loading }"
            >
                Submit
            </button>


        </div>
    </div>
</template>
<script>
import { ref, computed, watch} from 'vue';
import { S3Client, ListObjectsV2Command, GetObjectCommand } from '@aws-sdk/client-s3';

const BUCKET = 'ezra-pictures';
const FIRST_YEAR = 2024;
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default {
    name: 'EzraView',
    setup() {
        // State variables
        const password = ref(''); // To store user input password
        const credentials = ref(null); // To store fetched credentials
        const pictures = ref([]);
        const imageFilenames = ref([]);
        const loading = ref(false);
        const loadedIndexes = ref([]);
        const completedIndexes = ref([]);
        const selectedImage = ref(null);
        credentials.value = {}

        const passwordVisible = ref(true);
        const shake = ref(false);

        // Function to fetch credentials from Lambda
        const fetchCredentials = async (password) => {
            const endpoint = 'https://yp7rpq3hz4l6qpnj4gpvuxj43a0jkfwe.lambda-url.us-east-1.on.aws/';
            const body = { password };

            try {
                const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Origin': 'https://www.isaacrudich.com/'
                },
                body: JSON.stringify(body),
                });

                if (!response.ok) {
                throw new Error(`Error: ${response.status} ${response.statusText}`);
                }

                const data = await response.json();

                // Extract and store credentials
                credentials.value = {
                    accessKeyId: data.accessKeyId,
                    secretAccessKey: data.secretAccessKey,
                    sessionToken: data.sessionToken,
                    expiration: new Date(data.expiration),
                };
                return response
            } catch (error) {
                return response
            }
        };

        // Function to handle button click
        const handleFetchCredentials = async () => {
            loading.value = true;
            // Call fetchCredentials with the password entered by the user
            try {
                const response = await fetchCredentials(password.value);

                if (response.status === 200) {
                    passwordVisible.value = false;
                } else {
                    shake.value = true;
                    password.value = '';

                    // Remove shake animation after it's done (e.g., 500ms)
                    setTimeout(() => {
                        shake.value = false;
                    }, 500);
                }
            } catch (error) {
                shake.value = true;
                password.value = '';

                // Remove shake animation after it's done (e.g., 500ms)
                setTimeout(() => {
                    shake.value = false;
                }, 500);
                // console.log("Bad Credentials: ", credentials.value)
                console.error('Invalid credentials:', error);
            } finally {
                loading.value = false;
            }
        };

        const createClient = () => new S3Client({
            region: 'us-east-1',
            credentials: {
                accessKeyId: credentials.value.accessKeyId,
                secretAccessKey: credentials.value.secretAccessKey,
                sessionToken: credentials.value.sessionToken,
            },
        });

        // Find the year folders that actually exist in the bucket
        const listYears = async (client) => {
            const command = new ListObjectsV2Command({ Bucket: BUCKET, Delimiter: '/' });
            const response = await client.send(command);

            return (response.CommonPrefixes || [])
                .map(entry => entry.Prefix.replace(/\/$/, ''))
                .filter(name => /^\d{4}$/.test(name))
                .sort();
        };

        // Used only if the bucket cannot be listed at the top level
        const fallbackYears = () => {
            const years = [];
            for (let year = FIRST_YEAR; year <= new Date().getFullYear(); year++) {
                years.push(String(year));
            }
            return years;
        };

        const loadImageNames = async () => {
            try {
                const client = createClient();

                let years = [];
                try {
                    years = await listYears(client);
                } catch (error) {
                    console.error('Could not list the year folders, falling back to a year range:', error);
                }
                if (!years.length) {
                    years = fallbackYears();
                }

                let allFilenames = [];
                for (const year of years) {
                    const input = {
                        Bucket: BUCKET,
                        Prefix: `${year}/compressed/`
                    };

                    const command = new ListObjectsV2Command(input);
                    const response = await client.send(command);

                    // Extract only the filenames
                    const filenames = (response.Contents || []).map(object => object.Key).filter(key => !key.endsWith('/'));
                    allFilenames = allFilenames.concat(filenames);
                }

                imageFilenames.value = allFilenames;

                groupImagesByMonth();

                // Open on the most recent month
                const latestGroup = groupedImages.value.length - 1;
                if (latestGroup >= 0) {
                    selectedYear.value = groupedImages.value[latestGroup].startDate.getFullYear();
                    selectMonth(latestGroup);
                }
            } catch (error) {
                console.error('Failed to load the image names:', error);
            }
        };

        // Load the images for one month, only the first time it is opened.
        // Each picture is placed as soon as it arrives so the month fills in progressively.
        const loadGroup = async (groupIndex) => {
            if (loadedIndexes.value.includes(groupIndex)) {
                return;
            }
            loadedIndexes.value.push(groupIndex);

            const client = createClient();
            const filenames = groupedImages.value[groupIndex].imageNames;
            pictures.value[groupIndex] = new Array(filenames.length).fill(null);

            await Promise.all(filenames.map(async (file, index) => {
                try {
                    const getObjectCommand = new GetObjectCommand({ Bucket: BUCKET, Key: file });
                    const objectResponse = await client.send(getObjectCommand);

                    // Convert the image data to a blob URL
                    const blob = await objectResponse.Body.transformToByteArray();
                    pictures.value[groupIndex][index] = URL.createObjectURL(new Blob([blob]));
                } catch (error) {
                    console.error('Failed to load an image:', file, error);
                }
            }));

            completedIndexes.value.push(groupIndex);
        };

        // group the images by date for lazy loading
        const groupedImages = ref([]);
        const groupImagesByMonth = () => {
            const groups = [];
            let currentGroup = [];
            let groupStartDate = null;
            let endDate = null;

            for (const file of imageFilenames.value) {
                const [year, , date] = file.split('/');
                const [month, day] = date.split('_').map(Number);
                const fileDate = new Date(year, month - 1, day);

                if (!groupStartDate) {
                    groupStartDate = new Date(fileDate.getFullYear(), fileDate.getMonth(), 1);
                }

                // Check if file is in the same month as the current group
                if (fileDate.getFullYear() === groupStartDate.getFullYear() && fileDate.getMonth() === groupStartDate.getMonth()) {
                    currentGroup.push(file);
                } else {
                    // Create end date as last day of the month
                    endDate = new Date(groupStartDate.getFullYear(), groupStartDate.getMonth() + 1, 0);
                    groups.push({
                        imageNames: currentGroup,
                        startDate: groupStartDate,
                        endDate: endDate
                    });

                    // Start new group for the new month
                    currentGroup = [file];
                    groupStartDate = new Date(fileDate.getFullYear(), fileDate.getMonth(), 1);
                }
            }

            // Handle the last group
            if (currentGroup.length) {
                endDate = new Date(groupStartDate.getFullYear(), groupStartDate.getMonth() + 1, 0);
                groups.push({
                    imageNames: currentGroup,
                    startDate: groupStartDate,
                    endDate: endDate
                });
            }

            groupedImages.value = groups;

            for (let i = 0; i < groupedImages.value.length; i++) {
                pictures.value.push([]);
            }
        };

        // Year and month menu
        const selectedYear = ref(null);
        const selectedGroupIndex = ref(null);

        const years = computed(() => [...new Set(groupedImages.value.map(group => group.startDate.getFullYear()))].sort((a, b) => a - b));

        const monthsInSelectedYear = computed(() => groupedImages.value
            .map((group, index) => ({
                index,
                year: group.startDate.getFullYear(),
                label: MONTH_NAMES[group.startDate.getMonth()],
            }))
            .filter(month => month.year === selectedYear.value));

        const loadedCount = computed(() => (pictures.value[selectedGroupIndex.value] || []).filter(Boolean).length);

        const totalCount = computed(() => groupedImages.value[selectedGroupIndex.value]
            ? groupedImages.value[selectedGroupIndex.value].imageNames.length
            : 0);

        const isLoadingGroup = computed(() => selectedGroupIndex.value !== null
            && !completedIndexes.value.includes(selectedGroupIndex.value));

        const selectMonth = (groupIndex) => {
            selectedGroupIndex.value = groupIndex;
            selectedImage.value = null;
            loadGroup(groupIndex);
        };

        const selectYear = (year) => {
            if (year === selectedYear.value) {
                return;
            }
            selectedYear.value = year;

            // Open the most recent month of the year that was picked
            const months = monthsInSelectedYear.value;
            if (months.length) {
                selectMonth(months[months.length - 1].index);
            } else {
                selectedGroupIndex.value = null;
            }
        };

        const imageName = (groupIndex, imageIndex) => {
            const filenames = groupedImages.value[groupIndex] ? groupedImages.value[groupIndex].imageNames : [];
            return (filenames[imageIndex] || '').split('/').pop();
        };

        const selectImage = (groupIndex, imageIndex) => {
            if (selectedImage.value &&
                selectedImage.value.groupIndex === groupIndex &&
                selectedImage.value.imageIndex === imageIndex) {
                selectedImage.value = null;
            } else {
                selectedImage.value = { groupIndex, imageIndex };
            }
        };

        // Compressed images are always .webp, full resolution images are always .jpg
        const getHighResPath = (compressedPath) => compressedPath
            .replace('/compressed/', '/full_res/')
            .replace(/\.[^./]+$/, '.jpg');

        const downloadHighRes = async (groupIndex, imageIndex) => {
            try {
                const client = createClient();

                const compressedPath = groupedImages.value[groupIndex].imageNames[imageIndex];
                const highResPath = getHighResPath(compressedPath);

                const getObjectCommand = new GetObjectCommand({ Bucket: BUCKET, Key: highResPath });
                const objectResponse = await client.send(getObjectCommand);

                const blob = await objectResponse.Body.transformToByteArray();
                const imageBlob = new Blob([blob]);
                const url = URL.createObjectURL(imageBlob);

                const link = document.createElement('a');
                link.href = url;
                link.download = highResPath.split('/').pop();
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);

            } catch (error) {
                console.error('Error downloading high-res image:', error);
                alert('Failed to download high-res image. Please try again.');
            }
        };

        // Watch for changes in credentials and load the image names
        watch(credentials, (newCredentials) => {
            // Ensure the credentials are properly set
            if (newCredentials.accessKeyId && newCredentials.secretAccessKey) {
                loadImageNames();
            }
        }, { immediate: true });

    return {
        pictures,
        groupedImages,
        years,
        selectedYear,
        selectYear,
        monthsInSelectedYear,
        selectedGroupIndex,
        selectMonth,
        isLoadingGroup,
        loadedCount,
        totalCount,
        imageName,
        loading,
        password,
        passwordVisible,
        shake,
        handleFetchCredentials,
        selectedImage,
        selectImage,
        downloadHighRes,
    };
  },

}
</script>

<style>
    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        20%, 60% { transform: translateX(-5px); }
        40%, 80% { transform: translateX(5px); }
    }

    .animate-shake {
        animation: shake 0.5s ease-in-out;
    }
</style>
